import React from 'react';
import { getSafeDateTime } from 'helpers/date';
import { ff } from 'helpers/featureFlags';
import { stateFormErrorConnector } from 'components/Form/shared/stateFormErrors';
import classnames from 'classnames';
import ValidationElement from '../ValidationElement';
import NumberComponent from '../Number';
import Checkbox from '../Checkbox';
import Show from '../Show';
import SelectDropdown from '../SelectDropdown';
import { extractDate, validDate } from '../../Section/History/dateranges';
import DateControlValidator from '../../../validators/datecontrol';
import { setAttribute } from '../shared/setAttribute';

class DateControl extends ValidationElement {
  constructor(props) {
    super(props);
    const defaultMonth = props.month ? this.removeZeroPad(props.month) : '';
    const defaultDay = props.day ? this.removeZeroPad(props.day) : '';
    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      disabled: props.disabled,
      estimated: props.estimated,
      error: props.error,
      valid: props.valid,
      maxDate: props.maxDate,
      month: props.hideMonth ? '1' : defaultMonth,
      day: props.hideDay ? '1' : defaultDay,
      year: props.year,
      touched: this.isTouched(props.year, props.month, props.day),
      errors: [],
    };

    this.storeErrors = this.storeErrors.bind(this);
    this.beforeChange = this.beforeChange.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleErrorMonth = this.handleErrorMonth.bind(this);
    this.handleErrorDay = this.handleErrorDay.bind(this);
    this.handleErrorYear = this.handleErrorYear.bind(this);
    this.update = this.update.bind(this);
    this.updateMonth = this.updateMonth.bind(this);
    this.updateDay = this.updateDay.bind(this);
    this.updateYear = this.updateYear.bind(this);
    this.updateEstimated = this.updateEstimated.bind(this);
    this.handleDisable = this.handleDisable.bind(this);
    this.errors = [];
  }

  isTouched(year, month, day) {
    return month >= 1 && day >= 1 && year >= 1;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.disabled !== this.props.disabled
      || nextProps.day !== this.props.day
      || nextProps.month !== this.props.month
      || nextProps.year !== this.props.year
    ) {
      const {
        disabled, day, month, year
      } = this.state;
      const isDisabled = nextProps.disabled === true;

      // Sometimes the parent can provide a new value (such as
      // Relationships/Cohabitants - clear), which needs to update the state.
      // This will compare and when different, update the state. As there are
      // many updates, we only need to update the values when isDisabled is
      // specifically set to false. Otherwise, the handleDisable will not work
      // correctly and it'll generate false errors.
      if (
        (nextProps.day !== day
          || nextProps.month !== month
          || nextProps.year !== year)
        && !isDisabled
      ) {
        this.setState({
          ...{
            disabled,
            day,
            month,
            year,
          },
          day: nextProps.day,
          month: nextProps.month,
          year: nextProps.year,
        });
      }

      // Order matters here.
      if (nextProps.disabled !== this.state.disabled) {
        this.handleDisable(nextProps);
      }
    }
  }

  handleDisable(nextProps) {
    let updates = {};
    const errors = [...this.errors] || [];
    // If disabling component, set all errors to null
    if (nextProps.disabled) {
      this.errors = errors.map((err) => ({
        code: err.code,
        valid: null,
        uid: err.uid,
      }));
      updates = { month: '', day: '', year: '' };
    }
    this.props.onError('', this.errors);
    updates = {
      disabled: nextProps.disabled,
      ...updates,
    };
    this.setState(updates);
  }

  /**
   * @param {string} year
   * @param {string} month
   * @param {string} day
   * @param {string} estimated
   * @param {boolean} touched
   */
  update(year, month, day, estimated, touched) {
    this.setState(
      {
        month: this.props.hideMonth ? '1' : month,
        day: this.props.hideDay ? '1' : day,
        year,
        estimated,
        touched,
        error: false,
      },
      () => {
        this.props.onUpdate({
          name: this.props.name,
          month: this.props.hideMonth ? '1' : month,
          day: this.props.hideDay ? '1' : day,
          year,
          estimated,
          touched: touched || this.isTouched(year, month, day),
        });
      }
    );
  }

  updateMonth(event) {
    let year = new Date().getFullYear();
    if (this.state.year) {
      year = parseInt(this.state.year, 10);
    }
    const numberDays = event.target.value ? new Date(year, event.target.value, 0).getDate() : 31;

    this.update(
      this.state.year,
      event.target.value,
      parseInt(this.state.day, 10) > numberDays ? '' : this.state.day,
      this.state.estimated,
      this.state.touched
    );
  }

  updateDay(event) {
    this.update(
      this.state.year,
      this.state.month,
      event.target.value,
      this.state.estimated,
      this.state.touched
    );
  }

  updateYear(values) {
    const touched = values?.value?.length > 0;
    let year = new Date().getFullYear();
    if (values?.value) {
      year = parseInt(values.value, 10);
    }
    const numberDays = this.state.month
      ? new Date(year, parseInt(this.state.month, 10), 0).getDate()
      : 31;

    this.update(
      values.value,
      this.state.month,
      parseInt(this.state.day, 10) > numberDays ? '' : this.state.day,
      this.state.estimated,
      touched
    );
  }

  updateEstimated(values) {
    this.update(
      this.state.year,
      this.state.month,
      this.state.day,
      values.checked,
      this.state.touched
    );
  }

  handleErrorMonth(value, arr) {
    return this.handleError('month', value, arr);
  }

  handleErrorDay(value, arr) {
    return this.handleError('day', value, arr);
  }

  handleErrorYear(value, arr) {
    return this.handleError('year', value, arr);
  }

  handleError(code, value, arr) {
    const original = arr.map((err) => ({
      code: `date.${code}.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    // Handle required
    const arr2 = original.concat(
      this.constructor.errors
        .filter((err) => err.code === 'required')
        .map((err) => {
          const props = { ...this.props, ...this.state };
          return {
            code: `date.${err.code}`,
            valid: err.func(null, props),
            uid: this.state.uid,
          };
        })
    );

    // Introducing local state to the DateControl so it can determine
    // if there were **any** errors found in other child components.
    this.storeErrors(arr2, () => {
      // Get the full date if we can
      const date = validDate(this.state) ? extractDate(this.state) : null;

      const existingErr = this.errors.some((e) => e.valid === false);

      // If the date is valid and there are no child errors...
      let props = null;
      if (date && !existingErr) {
        // Prepare some properties for the error testing
        props = {
          ...this.props,
          ...this.state,
          validator: new DateControlValidator({ ...this.props, ...this.state }),
        };
      }

      // Call any `onError` binding with error checking specific to the `DateControl`
      let local = [];
      const noneRequiredErrors = this.constructor.errors.filter((err) => err.code !== 'required');
      local = noneRequiredErrors.map((err) => ({
        code: `${this.props.prefix ? this.props.prefix : 'date'}.${err.code}`,
        valid: props === null ? null : err.func(date, props),
        uid: this.state.uid,
      }));

      const hasLocalError = local.some((x) => x.valid === false);
      const hasRequiredError = arr2.some((x) => x.valid === false);
      let showError = false;
      if ((hasLocalError || hasRequiredError) && this.state.touched) {
        showError = true;
      }

      this.setState({ error: showError }, () => {
        // Pass any local and child errors to bound functions
        this.props.onError(date, [...arr2].concat(local));
      });
    });

    // Return the original array of errors to the child control
    return original;
  }

  storeErrors(arr = [], callback) {
    for (const e of arr) {
      const idx = this.errors.findIndex((x) => x.uid === e.uid && x.code === e.code);
      if (idx !== -1) {
        this.errors[idx] = { ...e };
      } else {
        this.errors.push({ ...e });
      }
    }

    this.setState({ errors: [...this.errors] }, () => {
      callback();
    });
  }

  beforeChange(value) {
    return value.replace(/\D/g, '');
  }

  monthDisplayText(value, text) {
    return `${value} (${text})`.trim();
  }

  getMonths = () => [
    { label: '', value: '' },
    { label: '01 - Jan', value: '1' },
    { label: '02 - Feb', value: '2' },
    { label: '03 - Mar', value: '3' },
    { label: '04 - Apr', value: '4' },
    { label: '05 - May', value: '5' },
    { label: '06 - Jun', value: '6' },
    { label: '07 - Jul', value: '7' },
    { label: '08 - Aug', value: '8' },
    { label: '09 - Sep', value: '9' },
    { label: '10 - Oct', value: '10' },
    { label: '11 - Nov', value: '11' },
    { label: '12 - Dec', value: '12' },
  ];

  getDays = () => {
    const safeDateTime = getSafeDateTime({
      month: this.state.month,
      year: this.state.year,
      day: '1',
    });
    const numberDays = safeDateTime.valid ? safeDateTime.datetime.daysInMonth : 31;

    return Array(1 + numberDays)
      .fill(0)
      .map((_, index) => (index === 0
        ? { label: '', value: '' }
        : { label: String(index).padStart(2, '0'), value: index }));
  };

  // TODO: Clean up. Will no longer be needed when we update all Dates to use date package (moment || luxor)
  removeZeroPad = (value) => {
    const newVal = parseInt(value, 10);
    return Number.isNaN(newVal) ? '' : newVal.toString();
  };

  render() {
    const maxYear = this.props.maxDate ? this.props.maxDate.year?.toString() : undefined;
    const minYear = this.props.useMinYear !== true ? '0' : this.props.minDate?.year?.toString() || '0';
    const isFFActive = ff(this.props.features).getActive('EN-4733');

    return (
      <div
        className={classnames(
          this.props.className,
          { datecontrol: !this.props.hideMonth || !this.props.hideDay },
          { 'usa-input-error': !isFFActive && this.state.error && !this.props.overrideError },
          { 'month-hidden': this.props.hideMonth },
          { 'day-hidden': this.props.hideDay }
        )}
      >
        <div className="datecontrol-container">
          <div
            className={classnames(
              {
                'usa-input-untouched-wrapper':
                  !isFFActive
                  && (!this.state.month || this.state.month.length === 0)
                  && !this.state.error,
              },
              { 'usa-input-error': !isFFActive && this.state.error && !this.props.overrideError },
              {
                'usa-input-success-wrapper':
                  !isFFActive && !this.state.error && this.state.month.length > 0,
              },
              {
                'usa-empty-format-wrapper': isFFActive,
              }
            )}
          >
            <div
              className={`usa-form-group month ${this.props.hideMonth === true ? 'hidden' : ''
              }`.trim()}
            >
              <SelectDropdown
                attribute={setAttribute(this.props.attribute, 'month')}
                attributeList={[
                  this.props.attribute,
                  setAttribute(this.props.attribute, 'month'),
                  ...this.props.attributeList,
                ]}
                name="month"
                label="Month"
                ariaPrepend={`Enter ${this.props.purposeLabel}`}
                className="option-list suffix usa-small-input"
                isDisabled={this.state.disabled}
                max="12"
                maxlength="2"
                min="1"
                readonly={this.props.readonly}
                required={!this.props.hideMonth && this.props.required}
                step="1"
                value={this.state.disabled ? '' : this.removeZeroPad(this.state.month)}
                error={this.state.error}
                onChange={this.updateMonth} // eslint-disable-line @typescript-eslint/unbound-method
                onError={this.handleErrorMonth} // eslint-disable-line @typescript-eslint/unbound-method
                id={`selectDropdown-Month-${this.state.uid}`}
              >
                {this.getMonths().map((option) => (
                  <option key={`month-${option.label}`} label={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectDropdown>
            </div>
          </div>
          <div
            className={classnames(
              {
                'usa-input-untouched-wrapper':
                  !isFFActive
                  && (!this.state.day || this.state.day.length === 0)
                  && !this.state.error,
              },
              { 'usa-input-error': !isFFActive && this.state.error && !this.props.overrideError },
              {
                'usa-input-success-wrapper':
                  !isFFActive && !this.state.error && this.state.day.length > 0,
              },
              {
                'usa-empty-format-wrapper': isFFActive,
              }
            )}
          >
            <div className={`usa-form-group day ${this.props.hideDay === true ? 'hidden' : ''}`}>
              <SelectDropdown
                attribute={setAttribute(this.props.attribute, 'day')}
                attributeList={[
                  this.props.attribute,
                  setAttribute(this.props.attribute, 'day'),
                  ...this.props.attributeList,
                ]}
                name="day"
                label="Day"
                ariaPrepend={`Enter ${this.props.purposeLabel}`}
                className="option-list suffix usa-small-input"
                isDisabled={this.state.disabled}
                readonly={this.props.readonly}
                step="1"
                value={this.state.disabled ? '' : this.removeZeroPad(this.state.day)}
                error={this.state.error}
                onChange={this.updateDay} // eslint-disable-line @typescript-eslint/unbound-method
                onError={this.handleErrorDay} // eslint-disable-line @typescript-eslint/unbound-method
                required={!this.props.hideDay && this.props.required}
                id={`selectDropdown-Day-${this.state.uid}`}
              >
                {this.getDays().map((option) => (
                  <option key={`day-${option.label}`} label={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectDropdown>
            </div>
          </div>
          <div
            className={classnames(
              {
                'usa-input-untouched-wrapper':
                  !isFFActive
                  && (!this.state.year || this.state.year.length === 0)
                  && !this.state.error,
              },
              { 'usa-input-error': !isFFActive && this.state.error && !this.props.overrideError },
              {
                'usa-input-success-wrapper':
                  !isFFActive && !this.state.error && this.state.year.length > 0,
              },
              {
                'usa-empty-format-wrapper': isFFActive,
              }
            )}
          >
            <div className="usa-form-group year">
              <NumberComponent
                attribute={setAttribute(this.props.attribute, 'year')}
                attributeList={[
                  this.props.attribute,
                  setAttribute(this.props.attribute, 'year'),
                  ...this.props.attributeList,
                ]}
                id="year"
                name="year"
                label="Year"
                ariaLabel={`Enter ${this.props.purposeLabel} Year`}
                disabled={this.state.disabled}
                min={minYear}
                max={maxYear}
                maxlength="4"
                pattern={this.props.pattern}
                readonly={this.props.readonly}
                step="1"
                value={this.state.disabled ? '' : this.props.year}
                error={this.state.error}
                onUpdate={this.updateYear} // eslint-disable-line @typescript-eslint/unbound-method
                onError={this.handleErrorYear} // eslint-disable-line @typescript-eslint/unbound-method
                required={this.props.required}
                prefix={this.props.prefix}
                valid={!this.state.error}
                forceValidation={this.props.forceYearValidation}
                makeStateFromErrors={this.props.makeYearStateFromErrors}
              />
            </div>
          </div>
        </div>
        <Show when={this.props.showEstimated}>
          <div className="flags">
            <Checkbox
              name="estimated"
              label="Estimated"
              ariaPrepend={`${this.props.purposeLabel} Date`}
              toggle="false"
              className="estimated"
              value={this.props.estimated}
              checked={this.state.estimated}
              disabled={this.state.disabled}
              onUpdate={this.updateEstimated} // eslint-disable-line @typescript-eslint/unbound-method
            />
          </div>
        </Show>
      </div>
    );
  }
}

DateControl.defaultProps = {
  attributeList: [],
  purposeLabel: '',
  name: 'datecontrol',
  disabled: false,
  estimated: false,
  showEstimated: true,
  overrideError: false,
  error: false,
  valid: false,
  hideDay: false,
  hideMonth: false,
  month: '',
  day: '',
  year: '',
  prefix: '',
  noMaxDate: false,
  maxDate: null,
  maxDateEqualTo: false,
  minDate: null,
  minDateEqualTo: false,
  forceMinDate: false,
  relationship: '',
  useMinYear: false,
  forceYearValidation: false,
  makeYearStateFromErrors: undefined,
  onUpdate: (values) => { },
  onError: (value, arr) => arr,
  tab: (el) => {
    el.focus();
  },
  notApplicable: false,
};

DateControl.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required) {
        return validDate(props);
      }
      return true;
    },
  },
  {
    code: 'max',
    func: (value, props) => {
      if (!value || Number.isNaN(value)) {
        return null;
      }
      return value && props.validator.validMaxDate();
    },
  },
  {
    code: 'min',
    func: (value, props) => {
      if (!value || Number.isNaN(value)) {
        return null;
      }
      return value && props.validator.validMinDate();
    },
  },
];

export { DateControl };
export default stateFormErrorConnector(DateControl);
